import { Button, Grid, makeStyles, Slider} from '@material-ui/core';
import { useEffect, useState } from 'react';
import { ISelectAllergens, ISelectFoodCategory } from '../../../interface/interface';
import { planEatApi } from '../../../planEatApi/PlanEatApi';
import { IDishContent } from '../../../planEatApi/PlanEatFacade';
import { AllergenButton } from '../../../components/ui/AllergenButton';
import { CategoryCheckbox } from '../../../components/ui/CategoryCheckbox';
import { allergensList } from '../../../constants/allergens';
import { foodCategoriesList } from '../../../constants/foodCategories';
import { sliderMarks } from '../../../constants/sliderValues';
import { errorToast, successToast } from '../../../utils/crudToast';
import { resetDish } from '../../../utils/resetObjects';
import { InputField } from '../../../components/ui/InputField';
import { InputContainer } from '../../../components/ui/InputContainer';
import { DISH_CO2_IMPACT_DEFAULT } from '../../../constants/constants';

const useStyles = makeStyles((theme: any) => ({
    title: {
        fontSize: 20,
        fontWeight: 700,
    }, 
    content:{
        width: '100%',
        background: '#FFFFFF',
        boxShadow: '0px 4px 4px rgba(11, 37, 89, 0.1)',
        borderRadius: 8,
        marginTop: 30,
        padding: 30
    },
    twoItemRow:{
        display: 'grid',
        gridTemplateColumns: '50% 50%',
        gridTemplateRow: '100%'
    },
    oneItemRow:{
        paddingTop: 40,
        display: 'grid',
        gridTemplateColumns: '100%',
        gridTemplateRow: '100%'
    },
    inputTitle:{
        fontSize: 18,
        fontWeight: 700,
        paddingBottom: 15
    },
    foodCategoryContainer:{
        display: 'grid',
        gridTemplateColumns: 'auto auto auto',
        columnGap: 15,
        rowGap: 15
    },
    textField:{
        width: '80%',
        height: '100%'
    },
    buttonContainer:{
        display: 'grid',
        justifyContent: 'flex-end',
        gridTemplateColumns: '15% 15%',
        gridTemplateRow: '100%',
        columnGap: 15,
        width: '100%',
        paddingTop: 20,
    },
    messageContainer: {
        height: 40,
        display: 'flex',
        width: "100%",
        margin: "auto",
        justifyContent: 'center'
    }
}));

export const AdminAddMenuItem: React.FC = () => {
    // Styling
    const classes = useStyles();

    // State
    const [allergensView, setAllergensView] = useState<any[]>([]);
    const [selectAllergens, setSelectAllergens] = useState<ISelectAllergens[]>([]);

    const [foodCategoryView, setFoodCategoryView] = useState<any[]>([]);
    const [selectFoodCategories, setSelectFoodCategories] = useState<ISelectFoodCategory[]>([]);

    const [dishObject, setDishObject] = useState<IDishContent>({
        id:"",
        name:"",
        description:"",
        officeId: "",
        dishTitle: "",
        dishDescription: "",
        foodCategories: [],
        allergens: [],
        co2Impact: DISH_CO2_IMPACT_DEFAULT
    });

    useEffect(() => {
        createSelectAllergens();
        createSelectFoodCategories();
    }, []);

    function createSelectAllergens() {
        const tmpSelectAllergens : ISelectAllergens[] = []
        for(let i=0;i<allergensList.length;i++){
            const tmpSelectAllergen : ISelectAllergens = {
                'allergen' : allergensList[i],
                'selected' : false
            }
            tmpSelectAllergens.push(tmpSelectAllergen)
        }
        setSelectAllergens(tmpSelectAllergens)
    }

    function createSelectFoodCategories() {
        const tmpSelectFoodCategories : ISelectFoodCategory[] = []
        for(let i=0;i<foodCategoriesList.length;i++){
            const tmpSelectFoodCategory : ISelectFoodCategory ={
                'foodCategory' : foodCategoriesList[i],
                'selected' : false
            }
            tmpSelectFoodCategories.push(tmpSelectFoodCategory)
        }
        setSelectFoodCategories(tmpSelectFoodCategories)
    }

    useEffect(() => {
        if(selectAllergens.length > 0){
            displayAllergens();
        }
      }, [selectAllergens]);

    useEffect(() => {
        if(selectFoodCategories.length > 0){
            displayFoodCategories();
        }
    }, [selectFoodCategories]);

    function displayAllergens() {
        const tmpAllergens = allergensList.map((allergens,index) => (
            <AllergenButton 
                index={index+1}
                selectAllergen={allergens}
                selectAllergens={selectAllergens}
                selected={selectAllergens[index].selected}
                setSelectAllergens={setSelectAllergens}
            /> 
        ));
        setAllergensView(tmpAllergens);
    }

    function displayFoodCategories() {
        const tmpFoodCategories = foodCategoriesList.map((foodCategory,index) => (
            <CategoryCheckbox 
                foodCategory={foodCategory}
                selectCategories={selectFoodCategories}
                selected={selectFoodCategories[index].selected}
                setSelectFoodCategories={setSelectFoodCategories}
            />
        ));
        setFoodCategoryView(tmpFoodCategories);
    }

    const handleChange = (prop: keyof IDishContent) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setDishObject({ ...dishObject, [prop]: event.target.value });
    };

    async function postDish() {
        let selectedAllergens : string[] = [];
        for(let i=0;i<selectAllergens.length;i++){
            if(selectAllergens[i].selected){
                selectedAllergens.push(selectAllergens[i].allergen)
            }
        }

        let selectedFoodcategories : string[] = [];
        for(let i=0;i<selectFoodCategories.length;i++){
            if(selectFoodCategories[i].selected){
                selectedFoodcategories.push(selectFoodCategories[i].foodCategory)
            }
        }
        dishObject.allergens = selectedAllergens;
        dishObject.foodCategories = selectedFoodcategories;

        await planEatApi.postDish(dishObject)
        .then((response) => {
            successToast("Dish",dishObject.id)
            resetCategories();
        }).catch ((e) => {
            errorToast("Dish",dishObject.id)
            console.log(e)
        })
    };

    function resetCategories() {
        // Reset through recreating
        setDishObject(resetDish)
        createSelectAllergens()
        createSelectFoodCategories()
    }

    return (
        <div>
            <div className={classes.title}>
                New Menu
            </div>
            <div className={classes.content}>
                <InputContainer>
                    <InputField
                        label="Dish Title"
                        variant="outlined"
                        multiline
                        rows={4}
                        value={dishObject.dishTitle}
                        onChange={handleChange('dishTitle')}
                        className={classes.textField}
                    />
                    <InputField
                        label="Dish Description"
                        variant="outlined"
                        multiline
                        rows={4}
                        value={dishObject.dishDescription}
                        onChange={handleChange('dishDescription')}
                        className={classes.textField}
                    />
                     <div>
                    <div className={classes.inputTitle}>Categories</div>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            className={classes.foodCategoryContainer}
                        >
                            {foodCategoryView}
                        </Grid>
                    </div>
                    <div>
                        <div className={classes.inputTitle}>Allergens</div>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                        >
                            {allergensView}
                        </Grid>
                    </div>
                    <div>
                        <div className={classes.inputTitle}>CO-2 impact</div>
                        <Slider
                            aria-label="CO2_slider"
                            defaultValue={3}
                            step={1}
                            min={1}
                            max={5}
                            marks={sliderMarks}
                            valueLabelDisplay="auto"
                        />
                    </div>
                </InputContainer>    
                <div>
                    <div className={classes.buttonContainer}>
                        <Button variant="contained">
                            Cancel
                        </Button>
                        <Button onClick={postDish} variant="contained">
                            Create
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}